import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { useAuth } from "../../../context/API_Client";
import UploadModal from "./UploadModal";
import { Button, Typography } from "@mui/material";
import UPLOAD_FILE_ICON from "../../../../assets/svg/UploadFileIcon.svg";
import FileDownload from "../../../../assets/svg/fileDownload.svg";
import UploadFileIconSidebar from "../../../../assets/svg/upload-file-icon-sidebar.svg";
import CustomButton from "../../../reusableComponents/CustomButton";
import { colorBrandObject } from "../../../../constants/brandings";
import BoxPayGrid from "../../BoxPayGrid";
import { ReconFileSchema } from "./ReconFileSchema";
import LoadingLogoPage from "../../loadingPage/loadingPage";
import {
  convertIsoToGivenDateFormat,
  generateDateTimezone,
  generateDateValueOf,
  isEmpty,
  PAGE_LIST,
} from "../../utils/utils";
import { customToast } from "../../../reusableComponents/customToaster/customToast";
import dayjs from "dayjs";
import Filter from "../../filters/filter";
import { filterForUploadReconSchema } from "../../filters/filterForUploadRecon/filterForUploadReconSchema";
import UploadFileSearch from "./uploadFileSearch";
import MonthYearPicker from "../../../reusableComponents/MonthYearPicker/MonthYearPicker";
import { RiArrowDropDownLine } from "react-icons/ri";
import FileSaver from "file-saver";
import { readableColor } from "polished";
import RollbackModal from "./RollbackModal";

function Files({ operationPermissions }) {
  const { reconId } = useParams();
  const [configDetails, setConfigDetails] = useState([]);
  const [toggleUploadModal, setToggleUploadModal] = useState(false);
  const [modalDetails, setModalDetails] = useState();
  const [loader, setLoader] = useState(true);
  const [dropDownInfo, setDropDownInfo] = useState([]);
  const [allFiltersDropDown, setAllFiltersDropDown] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [timeZone, setTimeZone] = useState(null);
  const [searchLoader, setSearchLoader] = useState(false);
  const [filterModalData, setFilterModalData] = useState([]);
  const [dateModalData, setDateModalData] = useState([]);
  const [allFilters, setAllFilters] = useState({});
  const [pageNo, setPageNo] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIST[2]);
  const [totalDataCount, setTotalDataCount] = useState(100);
  const [openDateModal, setOpenDateModal] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [searchAttributes, setSearchAttributes] = useState({});
  const firstUpdateRef = useRef();
  const [activeTab, setActiveTab] = React.useState('upload_file');
  const [currentFileName, setCurrentFileName] = useState("")

  const {
    getGenericReconciliationRecordFiles,
    isAuthorizedPermission,
    downloadFileUrlOpen,
    downloadInvalidReconFile,
    merchantSettings,
    getGenericReconciliationVisualizationData,
    downloadProcessedVisualData,
    getGenericReconciliationConfig
  } = useAuth();

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const memoizedFilterModalData = useMemo(() => filterModalData, [filterModalData]);
  const memoizedDateModalData = useMemo(() => dateModalData, [dateModalData]);
  const memoizedDropDownInfo = useMemo(() => dropDownInfo, [dropDownInfo]);
  const memoizedConfigDetails = useMemo(() => configDetails, [configDetails]);
  useEffect(() => {
    if (!firstUpdateRef.current) {
      firstUpdateRef.current = true;
      return;
    }
    if (
      !isEmpty(memoizedConfigDetails) &&
      !isEmpty(memoizedDropDownInfo) &&
      !isEmpty(timeZone)
    ) {
      getFilteredData(searchAttributes.attrName, searchAttributes.values);
    }
  }, [
    timeZone,
    memoizedFilterModalData,
    memoizedDateModalData,
    pageNo,
    pageLimit,
    memoizedConfigDetails,
    memoizedDropDownInfo,
  ]);

  const handleResetData = () => {
    if (firstUpdateRef.current) {
      if (
        !isEmpty(memoizedConfigDetails) &&
        !isEmpty(memoizedDropDownInfo) &&
        !isEmpty(timeZone)
      ) {
        firstUpdateRef.current = false
        setDropDownInfo([])
        setConfigDetails([])
      }
    }
  }
  useEffect(() => {
    handleResetData()
  }, [reconId])

  useEffect(() => {
    if (isEmpty(dropDownInfo)) return;
    handleFilterSchema(filterForUploadReconSchema, allFiltersDropDown);
  }, [allFiltersDropDown]);

  const handleFilterSchema = (schema, mainArray) => {
    let tempSchema = [...schema];
    tempSchema.forEach((obj) => {
      let data = mainArray.find(m => m.filterName === obj.id);
      if (!isEmpty(data)) {
        obj.items = data.filterValues;
      }
    });
  }

  const generatePayload = (attrName, values) => {
    let search = [];
    if (Array.isArray(values) || values?.length > 0) {
      search.push({
        attrName: attrName,
        operator: "containsIgnoreCase",
        values: [values],
      });
    }

    const toDate = new Date();
    toDate.setHours(23, 59, 59, 999);
    let fromDate = new Date(toDate);
    fromDate.setMonth(fromDate.getMonth() - 13);
    fromDate.setHours(0, 0, 0, 0);

    let filters = {};
    filterModalData.forEach((item) => {
      filters[item.id] = item.value;
    });

    if (dateModalData?.fromDate) {
      search.push({
        "attrName": "recordsTimestamp",
        "operator": "lte",
        "values": [dateModalData?.toDate]
      },)
      search.push({
        "attrName": "recordsTimestamp",
        "operator": "gte",
        "values": [dateModalData?.fromDate]
      },)
    }

    return {
      fromDate: fromDate,
      toDate: toDate,
      limit: pageLimit,
      offset: (pageNo - 1) * pageLimit,
      attributeFilters: filters,
      advancedAttributeFilters: search,
      filters: ["status", "fileSetCode"],
    };
  };

  const getFilteredData = (attrName, values) => {
    setSearchLoader(true);
    const payload = generatePayload(attrName, values);
    getGenericReconciliationVisualizationData(
      "generic_recon_file_processing_info",
      reconId,
      payload,
      function (data) {
        setSearchLoader(false);
        setLoader(false);
        setTotalDataCount(data?.totalCount);
        getFormatData(data?.points);
        if (!isEmpty(data?.filters)) {
          setAllFilters(data.filters);
        }
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
        setSearchLoader(false);
        setLoader(false);
      }
    );
  };

  const handleCloseUploadModal = () => {
    setToggleUploadModal(false);
  };

  const getFormatData = (data) => {
    let formattedData = [];
    if (data?.length) {
      formattedData = data?.map((singleData) => {
        if (singleData?.updatedOn) {
          singleData.updatedOnConverted = convertIsoToGivenDateFormat(singleData.updatedOn, timeZone);
        }
        if (singleData?.addedOn) {

          singleData.addedOnConverted = convertIsoToGivenDateFormat(singleData.addedOn, timeZone);
        }
        if (singleData?.fileSetCode) {
          const fileSet = dropDownInfo?.find((el) => el?.fileSetCode?.toLowerCase() == singleData?.fileSetCode?.toLowerCase());
          const fileType = configDetails?.find((el) => el.fileTypeCode?.toLowerCase() == fileSet?.fileTypeCode?.toLowerCase());
          singleData.fileType = fileType?.fileTypeTitle ? fileType.fileTypeTitle : "";
          singleData.source = fileSet?.fileSetTitle ? fileSet.fileSetTitle : "";
        }
        if (singleData?.recordsTimestamp) {
          singleData.recordsTimestamp = dayjs(singleData?.recordsTimestamp).format("MMMM YYYY");
        }
        return singleData;
      });
      setTableData(formattedData);
    } else {
      setTableData([]);
    }
  };

  const getGenericReconciliationsConfigData = async () => {
    await getGenericReconciliationConfig(
      function (data) {
        let reconConfig = data.find(d => d.reconId === reconId);
        return setConfigDetails(reconConfig?.fileTypeConfigs || []);
      },
      function (errMsg) {
        customToast("error", "high", errMsg);
      }
    );
  }

  const getBasicDetails = async () => {
    let reconConfigString = localStorage.getItem("recon-config");
    try {
      let reconConfigParsed = JSON.parse(reconConfigString);
      if (!isEmpty(reconConfigParsed)) {
        let reconConfig = reconConfigParsed.find(d => d.reconId === reconId);
        setConfigDetails(reconConfig?.fileTypeConfigs);
      } else {
        getGenericReconciliationsConfigData();
      }
    } catch (error) {
      getGenericReconciliationsConfigData();
    }

    await getGenericReconciliationRecordFiles(
      reconId,
      function (data) {
        if (data) {
          setDropDownInfo(data);
        }
      },
      function (errMsg) {
        customToast("error", "high", errMsg);
      }
    );
  };

  const getFilteredDataAsync = () => {
    getGenericReconciliationVisualizationData(
      "filters/generic_recon_file_processing_info",
      reconId,
      {},
      function (data) {
        setLoader(false)
        setAllFiltersDropDown(data)
      },
      function (errorMsg) {
        setLoader(false)
        customToast("error", "high", errorMsg);
      }
    );
  }

  useEffect(() => {
    getBasicDetails();
    getFilteredDataAsync();
  }, [reconId]);

  const handleRevertFile = (fileName) => {
    setCurrentFileName(fileName)

  };

  const renderButton = (item) => {
    return (
      <CustomButton
        key={item.fileTypeCode}
        text={`Upload ${item.fileTypeTitle}`}
        type={"primary"}
        size={"md"}
        variant={"contained"}
        iconButton={true}
        onClick={() => handleModalOpen(item)}
      />
    );
  };

  const handleModalOpen = (data) => {
    setToggleUploadModal(true);
    setModalDetails(data);
  };

  const invalidFileDownload = async (filename) => {
    downloadFileUrlOpen(downloadInvalidReconFile(filename));
  };

  useEffect(() => {
    merchantSettings(
      function (data) {
        setTimeZone(data?.timeZone);
      },
      function (errorMsg) { }
    );
  }, [reconId]);

  const handleSearchbarModalData = (data) => {
    if (filterModalData?.length == 0 && data?.length == 0) return;
    setFilterModalData(data);
  };

  const handleDateModalData = (data) => {
    // if (value === 0) {
    //   setDateModalData(data);
    // } else {
    //   setDateModalDataForRefund(data);
    // }
  };

  const handleSearchData = (attrName, values) => {
    setPageNo(1);
    setSearchAttributes({ attrName, values });
    getFilteredData(attrName, values);
  };

  const handleDateChange = (date, type) => {
    setOpenDateModal(false);
    if (!date) return setDateModalData({});
    let formattedStartDate = generateDateValueOf("start", date);
    let formattedEndDate = generateDateValueOf("end", date);
    setDateModalData({
      fromDate: formattedStartDate,
      toDate: formattedEndDate,
      value: date
    });
  };

  const handleDownloadClick = () => {
    setButtonLoader(true);
    const payload = generatePayload(
      searchAttributes.attrName,
      searchAttributes.values
    );
    downloadProcessedVisualData(
      payload,
      function (data) {
        const contentDisposition = data.headers["content-disposition"];
        const match = contentDisposition.match(/filename="(.+?)"/);
        const contentType = data.headers["content-type"];

        let filename;
        if (match && match[1]) {
          filename = match[1];
        } else {
          filename = "Data.csv";
        }
        const blob = new Blob([data.data], {
          type: contentType,
        });
        setButtonLoader(false);
        FileSaver.saveAs(blob, filename);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
        setButtonLoader(false);
      }
    );
  };

  return (
    <div className="content-wrappers" style={{ background: "white" }}>
      {loader ? (
        <LoadingLogoPage />
      ) : (
        <>
          {tableData != undefined &&
            isAuthorizedPermission(operationPermissions.tablePermission) ? (
            <section className="contents">
              <div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        color: colorBrandObject?.sideBarSelectedOptionBackground,
                        height: "24px",
                        width: "24px",
                        marginTop: "-2px",
                        marginRight: "4px",
                      }}
                    >
                      <UploadFileIconSidebar
                        width={24}
                        height={24}
                        style={{ marginRight: "5px" }}
                      />
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Typography className="subHeaderMedium font-black-highest">
                          Reconciliation
                        </Typography>
                      </div>
                      <div style={{ display: "flex" }}>
                        <Typography
                          className="bodyB2RegularText font-black-high"
                          style={{ marginTop: "6px" }}
                        >
                          Get a handle of all files in a batch view
                        </Typography>
                      </div>
                    </div>
                  </div>

                  <div
                    className="mb-3"
                    style={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    {isAuthorizedPermission(operationPermissions?.uploadRecord) && configDetails?.length > 0 &&
                      configDetails.map((item) => {
                        return renderButton(item);
                      })}

                    <CustomButton
                      text={<FileDownload />}
                      sx={{ padding: "0" }}
                      onClick={handleDownloadClick}
                      loading={buttonLoader}
                      color={"primary"}
                    />
                  </div>
                </div>

                <div
                  className="btn-group"
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    marginTop: "15px",
                  }}
                >
                  <div style={{ width: "70%" }}>
                    <UploadFileSearch searchData={handleSearchData} />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    margin: "24px 0",
                    justifyContent: "flex-start",
                    width: "100%",
                  }}
                >
                  <Filter
                    value={"value"}
                    filteredSchema={filterForUploadReconSchema}
                    handleSearchbarModalData={handleSearchbarModalData}
                    handleDateModalData={handleDateModalData}
                  />

                  <Button
                    onClick={() => setOpenDateModal(true)}
                    endIcon={<RiArrowDropDownLine className="modalCrossIcon" />}
                    style={{
                      borderRadius: "44px",
                      padding: "16px",
                      border: "2px solid black",
                      color: "rgba(1, 1, 2, 0.72)",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "poppins",
                      textTransform: "none",
                      marginRight: "12px",
                      border: "1px solid rgba(1, 1, 2, 0.72)",
                      width: "fit-content",
                      height: "40px",
                      marginLeft: "12px",
                    }}
                    focusRipple={false}
                    disableRipple={true}
                  >
                    {dateModalData?.value && <div className="options-selected" style={{
                      color: readableColor(colorBrandObject?.secondaryButtonColor),
                      backgroundColor: colorBrandObject?.secondaryButtonColor
                    }}>1</div>}

                    Month
                  </Button>

                  {openDateModal && <MonthYearPicker
                    label="Pick a Month and Year *"
                    disableFuture={true}
                    showTextField={false}
                    isOpen={openDateModal}
                    value={dateModalData?.value}
                    isClearDate={true}
                    onChange={(date) => handleDateChange(date)}
                    setIsOpen={setOpenDateModal}
                    primaryButton={"Apply"}

                  />}
                </div>

                <div div style={{ width: "100%" }}>
                  <BoxPayGrid
                    schema={ReconFileSchema(
                      configDetails,
                      dropDownInfo,
                      operationPermissions,
                      invalidFileDownload,
                      handleRevertFile,
                      timeZone
                    )}
                    data={tableData}
                    loader={searchLoader}
                    customPagination={true}
                    pageNo={pageNo}
                    setPageNo={setPageNo}
                    pageLimit={pageLimit}
                    setPageLimit={setPageLimit}
                    totalDataCount={totalDataCount}
                  />
                </div>
              </div>

            </section>
          ) : (
            <>
              {isAuthorizedPermission(operationPermissions?.uploadRecord) ? (
                <div className="roundedBorder mx-4 my-5 text-center  p-5">
                  <img src={UPLOAD_FILE_ICON} style={{ marginRight: "5px" }} />
                  <Typography className="subHeaderMedium font-black-highest text-center my-3">
                    Upload Your File
                  </Typography>

                  {configDetails?.length > 0 && (
                    <div
                      className="mb-3"
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                      }}
                    >
                      {configDetails.map((item) => {
                        return renderButton(item);
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "130px",
                  }}
                >
                  <Typography className="subHeaderMedium">
                    No data to display
                  </Typography>
                </div>
              )}
            </>
          )}
        </>
      )
      }

      {currentFileName && <RollbackModal modalData={currentFileName} setModalData={setCurrentFileName} getFilteredData={() => getFilteredData(searchAttributes.attrName, searchAttributes.values)} />}
      {
        toggleUploadModal && dropDownInfo?.length > 0 && (
          <UploadModal
            dropDownInfo={dropDownInfo}
            handleCloseUploadModal={handleCloseUploadModal}
            toggleDialog={toggleUploadModal}
            modalDetails={modalDetails}
            getTableData={() =>
              getFilteredData(searchAttributes.attrName, searchAttributes.values)
            }
          />
        )
      }
    </div >
  );
}

export default Files;
